/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React, {forwardRef, useCallback, useRef, useState} from 'react';
import {ColDef} from 'ag-grid-community';
import {translate} from '@ion/core/src/locale/locale';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {resolveDefinitions} from '@ion/dls/src/grid/ColumnResolver';
import IonGrid, {IonGridAPI, RowModelTypes} from '@ion/dls/src/grid/IonGrid';
import {PAGE_SIZES} from '@ion/dls/src/grid/settings';
import {useColumnChooser} from '@ion/dls/src/grid/useColumnChooser';
import {useColumnState} from '@ion/dls/src/grid/useColumnState';
import {setVehicleHistoryGridColumn} from './store/store';

export interface VehicleHistoryGridProps {
  vehicleHistoryData: Array;
  loading?: boolean;
}

const VehicleHistoryGrid = forwardRef<IonGridAPI, VehicleHistoryGridProps>(({
  vehicleHistoryData,
  loading
}, ref) => {
  const columnState           = setVehicleHistoryGridColumn;
  const gridRef               = useRef<IonGridAPI>(null);
  const [gridApi, setGridApi] = useState(null);
  const gridPageSize          = 20;
  const totalRows             = Object.keys(vehicleHistoryData)?.length;

  const {definitions, defaultDefinitions} = useColumnState({
    columnDefinitions : getColumnDefinitions(),
    columnState
  });

  const {columnDefinitions} = useColumnChooser(definitions);

  const constructGridRows = useCallback((vehicleHistoryData) => {
    if (vehicleHistoryData?.length) {
      return vehicleHistoryData?.map(({vehicleIdHistory, profileVersion}) => {
        const {
          yearString,
          makeString,
          modelString,
          engineString,
          vin,
          params,
          dateTriggered,
          _id,
          esn,
          mobileId
        } = vehicleIdHistory || {};
        return {
          pidIndicatorSupport : params?.split(',')?.length || 0,
          ymm                 : [yearString, makeString, modelString].join(' '),
          engine              : engineString,
          esn                 : (esn === 0) ? mobileId : esn,
          messageUUID         : _id,
          eventTime           : formatDateTime(dateTriggered),
          profileVersion,
          vin
        };
      });
    }
  }, []);

  return (
    <IonGrid ref={gridRef}
      isLoading={loading}
      onColumnChooserClose={function() { }}
      rowData={vehicleHistoryData}
      totalRows={totalRows}
      rowModelType={RowModelTypes.ClientSide}
      defaultDefinitions={defaultDefinitions}
      checkboxesEnabled={false}
      columnDefinitions={columnDefinitions}
      onGridReady={setGridApi}
      augmentItems={constructGridRows}
      pageSizes={PAGE_SIZES}
      pageSize={gridPageSize}
      showCustomPaginationBar
      paginationEnabled
      omitHeader />
  );
}
);

const defaultColumn = (headerName: string, field: string): ColDef => ({
  headerName,
  field,
  resizable : true,
  hide      : false
});

/**
 * Returns column definitions for the desktop grid implementation ...
 */
const getCommonColumnDefinitions = (): ColDef[] => {
  const prefix = 'vehicle-manager:vehicleHistoryGrid.grid';
  return [
    defaultColumn(translate(`${prefix}.ymm`), 'ymm'),
    defaultColumn(translate(`${prefix}.engine`), 'engine'),
    defaultColumn(translate(`${prefix}.vin`), 'vin'),
    defaultColumn(translate(`${prefix}.profileVersion`), 'profileVersion'),
    defaultColumn(translate(`${prefix}.pidIndicatorSupport`), 'pidIndicatorSupport'),
    defaultColumn(translate(`${prefix}.esn`), 'esn'),
    defaultColumn(translate(`${prefix}.messageUUID`), 'messageUUID'),
    {
      headerName : translate(`${prefix}.eventTime`),
      field      : 'eventTime',
      sort       : 'desc',
      sortable   : false,
      resizable  : true,
      hide       : false
    }
  ];
};

const getColumnDefinitions = () => {
  const columns = resolveDefinitions(getCommonColumnDefinitions());
  return ({
    desktop : columns,
    mobile  : columns
  });
};

export default VehicleHistoryGrid;
